/**
 * Central configuration file for the web UI
 * 
 * This file defines the domain and port settings for all server components.
 * Edit this file to change server configurations without modifying application code.
 */

// Environment detection
const environment = process.env.NODE_ENV || 'development';

// Server configuration
const servers = {
  development: {
    proxy: {
      protocol: 'http',
      domain: 'localhost',
      port: 3001,
      path: ''
    },
    website: {
      protocol: 'http',
      domain: 'localhost',
      port: 3002,
      path: ''
    },
    imageProcessing: {
      protocol: 'http',
      domain: 'localhost',
      port: 3003,
      path: ''
    },
    stripe: {
      protocol: 'http',
      domain: 'localhost',
      port: 3004,
      path: ''
    }
  },
  production: {
    proxy: {
      protocol: 'https',
      domain: 'api.geteditify.com',
      port: 3001, // Updated to match the actual running port
      path: ''
    },
    website: {
      protocol: 'https',
      domain: 'geteditify.com',
      port: 3002,
      path: ''
    },
    imageProcessing: {
      protocol: 'https',
      domain: 'imageserver.geteditify.com',
      port: 3003,
      path: ''
    },
    stripe: {
      protocol: 'https',
      domain: 'stripe.geteditify.com',
      port: 3004,
      path: ''
    }
  }
};

// Firebase configuration
const firebase = {
  development: {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID
  },
  production: {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID
  }
};

/**
 * Get the full URL for a specific server type
 * @param {string} serverType - The type of server (proxy, website, imageProcessing, stripe)
 * @returns {string} The complete URL including protocol, domain, and port
 */
function getServerUrl(serverType) {
  const env = environment;
  const server = servers[env][serverType];
  
  let url = `${server.protocol}://${server.domain}`;
  
  // Add port if specified and not the default for the protocol
  // In development mode, always include the port
  // In production mode, only include the port if it's not a standard port (80 for HTTP, 443 for HTTPS)
  // Never include port for proxy server in production mode
  if (server.port && (
    (env === 'development') || 
    (env === 'production' && serverType !== 'proxy' && (
      (server.protocol === 'http' && server.port !== 80) || 
      (server.protocol === 'https' && server.port !== 443)
    ))
  )) {
    url += `:${server.port}`;
  }
  
  // Add path if specified
  if (server.path) {
    // Ensure path starts with a slash
    const path = server.path.startsWith('/') ? server.path : `/${server.path}`;
    url += path;
  }
  
  return url;
}

/**
 * Get the port for a specific server type
 * @param {string} serverType - The type of server (proxy, website, imageProcessing, stripe)
 * @returns {number|null} The port number or null if using default port
 */
function getServerPort(serverType) {
  const env = environment;
  return servers[env][serverType].port;
}

/**
 * Get the domain for a specific server type
 * @param {string} serverType - The type of server (proxy, website, imageProcessing, stripe)
 * @returns {string} The domain
 */
function getServerDomain(serverType) {
  const env = environment;
  return servers[env][serverType].domain;
}

/**
 * Get the protocol for a specific server type
 * @param {string} serverType - The type of server (proxy, website, imageProcessing, stripe)
 * @returns {string} The protocol (http or https)
 */
function getServerProtocol(serverType) {
  const env = environment;
  return servers[env][serverType].protocol;
}

/**
 * Get the path for a specific server type
 * @param {string} serverType - The type of server (proxy, website, imageProcessing, stripe)
 * @returns {string} The path
 */
function getServerPath(serverType) {
  const env = environment;
  return servers[env][serverType].path;
}

/**
 * Get the Firebase configuration for the current environment
 * @returns {object} The Firebase configuration object
 */
function getFirebaseConfig() {
  return firebase[environment];
}

module.exports = {
  environment,
  servers,
  firebase,
  getServerUrl,
  getServerPort,
  getServerDomain,
  getServerProtocol,
  getServerPath,
  getFirebaseConfig
};
